import { ContestResponseData } from "@/restapi/contest";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type DateRange = {
  start: dayjs.Dayjs | null;
  end: dayjs.Dayjs | null; // null = 終了未定（無期限）
};

type Locale = "ja" | "en";

type RangeFormatOptions = {
  locale?: Locale;

  // ja: YYYY/MM/DD, en: MMM D ’YY を基本にする
  jaFormat?: string;
  enFormatWithYear?: string;   // 例: "MMM D ’YY"
  enFormatNoYear?: string;     // 例: "MMM D"

  // 区切り
  jaSeparator?: string;        // " 〜 "
  enSeparator?: string;        // " – "

  // 期限未定（end=null）
  openEndedLabel?: string;     // ja: "未定", en: "TBD"

  // 英語で年を省略するルール
  // - start/end が同じ年なら start側の年は省略
  // - start/end が違う年なら両方に年
  smartYear?: boolean;
};

export class ContestService {
  static isFinished = (contest: ContestResponseData): boolean => {
    const end = dayjs(contest.contestEndDate);
    const today = dayjs();
    return end.isBefore(today, "day");
  };

  static isOngoing = (contest: ContestResponseData): boolean => {
    const { startDate, scoringStartDate } = contest;
    if (!startDate) return false;

    const today = dayjs();
    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return false;

    if (scoringStartDate) {
      const end = dayjs(scoringStartDate, "YYYY/MM/DD");
      if (!end.isValid()) return true;
      return start.isSameOrBefore(today, "day") && end.isSameOrAfter(today, "day");
    }

    return start.isSameOrBefore(today, "day");
  };

  static isUpcoming = (contest: ContestResponseData): boolean | null => {
    const { startDate } = contest;
    if (!startDate) return null;

    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return null;

    return start.isAfter(dayjs(), "day");
  };

  /**
  * 開始日を指定フォーマットで返す（存在しない場合は null）
  */
  static getFormattedStartDate = (contest: ContestResponseData, format = "YYYY/MM/DD"): string | null => {
    if (!contest.startDate) return null;
    const start = dayjs(contest.startDate);
    return start.isValid() ? start.format(format) : null;
  };

  /**
  * 終了日を指定フォーマットで返す（存在しない場合は null）
  */
  static getFormattedEndDate = (contest: ContestResponseData, format = "YYYY/MM/DD"): string | null => {
    if (!contest.scoringStartDate) return null;
    const end = dayjs(contest.scoringStartDate);
    return end.isValid() ? end.format(format) : null;
  };

  /**
* 写真投稿受付中かを判定する
* - startDate <= today < scoringStartDate
*/
  static isSubmissionOpen = (contest: ContestResponseData): boolean => {
    const { startDate, scoringStartDate } = contest;
    if (!startDate) return false;

    const today = dayjs();
    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return false;

    if (!scoringStartDate) return start.isSameOrBefore(today, "day");

    const scoringStart = dayjs(scoringStartDate, "YYYY/MM/DD");
    if (!scoringStart.isValid()) return true;

    return start.isSameOrBefore(today, "day") && today.isBefore(scoringStart, "day");
  };

  /**
   * 採点受付中かを判定する
   * - scoringStartDate <= today <= contestEndDate
   */
  static isScoringOpen = (contest: ContestResponseData): boolean => {
    const { scoringStartDate, contestEndDate } = contest;
    if (!scoringStartDate) return false;

    const today = dayjs();
    const scoringStart = dayjs(scoringStartDate, "YYYY/MM/DD");
    if (!scoringStart.isValid()) return false;

    if (!contestEndDate) return scoringStart.isSameOrBefore(today, "day");

    const end = dayjs(contestEndDate, "YYYY/MM/DD");
    if (!end.isValid()) return true;

    return scoringStart.isSameOrBefore(today, "day") && end.isSameOrAfter(today, "day");
  };
  /**
 * コンテストが開催前かを判定する
 * - today < startDate
 */
  static isBeforeSubmission = (contest: ContestResponseData): boolean => {
    const { startDate } = contest;
    if (!startDate) return false;

    const today = dayjs();
    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return false;

    return today.isBefore(start, "day");
  };

  /**
  * 投稿受付期間（startDate 〜 scoringStartDateの前日）
  * - scoringStartDate が無い場合：終了未定（end=null）
  */
  static getSubmissionDateRange = (contest: ContestResponseData): DateRange => {
    const { startDate, scoringStartDate } = contest;
    if (!startDate) return { start: null, end: null };

    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return { start: null, end: null };

    if (!scoringStartDate) return { start, end: null };

    const scoringStart = dayjs(scoringStartDate, "YYYY/MM/DD");
    if (!scoringStart.isValid()) return { start, end: null };

    return { start, end: scoringStart.subtract(1, "day") };
  };


  /**
   * 採点受付期間（scoringStartDate 〜 contestEndDate）
   * - contestEndDate が無い場合：終了未定（end=null）
   */
  static getScoringDateRange = (contest: ContestResponseData): DateRange => {
    const { scoringStartDate, contestEndDate } = contest;
    if (!scoringStartDate) return { start: null, end: null };

    const start = dayjs(scoringStartDate, "YYYY/MM/DD");
    if (!start.isValid()) return { start: null, end: null };

    if (!contestEndDate) return { start, end: null };

    const end = dayjs(contestEndDate, "YYYY/MM/DD");
    if (!end.isValid()) return { start, end: null };

    return { start, end };
  };
  /**
   * 期間表示用：YYYY/MM/DD 〜 YYYY/MM/DD 形式で返す
   * - end が null の場合は「〜」の右側を "未定" にする（任意で変更可）
   */
  static formatDateRange = (
    range: DateRange,
    format = "YYYY/MM/DD",
    openEndedLabel = "未定"
  ): string | null => {
    if (!range.start) return null;

    const startStr = range.start.format(format);
    const endStr = range.end ? range.end.format(format) : openEndedLabel;

    return `${startStr} 〜 ${endStr}`;
  };
  /**
   * 投稿受付期間の表示文字列を返す（例: 2026/01/10 〜 2026/01/19）
   */
  static formatDateRangeSmart = (range: DateRange, opts: RangeFormatOptions = {}): string | null => {
    if (!range.start) return null;

    const {
      locale = "ja",
      jaFormat = "YYYY/MM/DD",
      enFormatWithYear = "MMM D ’YY",
      enFormatNoYear = "MMM D",
      jaSeparator = " 〜 ",
      enSeparator = " – ",
      openEndedLabel = locale === "ja" ? "未定" : "TBD",
      smartYear = true,
    } = opts;

    const s = range.start;
    const e = range.end;

    if (locale === "ja") {
      const startStr = s.format(jaFormat);
      const endStr = e ? e.format(jaFormat) : openEndedLabel;
      return `${startStr}${jaSeparator}${endStr}`;
    }

    // locale === "en"
    if (!e) {
      // 期限未定: "Jan 10 ’26 – TBD"
      return `${s.format(enFormatWithYear)}${enSeparator}${openEndedLabel}`;
    }

    const sameYear = s.year() === e.year();

    // 同年なら start側の年を省略（smartYear=true）
    const startFmt = smartYear && sameYear ? enFormatNoYear : enFormatWithYear;
    const endFmt = enFormatWithYear;

    return `${s.format(startFmt)}${enSeparator}${e.format(endFmt)}`;
  };

  static getSubmissionPeriodLabel = (
    contest: ContestResponseData,
    format = "YYYY/MM/DD",
    openEndedLabel = "未定"
  ): string | null => {
    // 互換維持（現状の挙動）
    return this.formatDateRange(this.getSubmissionDateRange(contest), format, openEndedLabel);
  };
  /**
   * 採点受付期間の表示文字列を返す（例: 2026/01/20 〜 2026/01/31）
   */
  static getScoringPeriodLabel = (
    contest: ContestResponseData,
    format = "YYYY/MM/DD",
    openEndedLabel = "未定"
  ): string | null => {
    // 互換維持（現状の挙動）
    return this.formatDateRange(this.getScoringDateRange(contest), format, openEndedLabel);
  };


  /**
 * 開催前（近日公開）のラベルを返す
 * 例: "2026/01/10〜"
 */
  static getSubmissionPeriodLabelSmart = (
    contest: ContestResponseData,
    opts: RangeFormatOptions = {}
  ): string | null => {
    return this.formatDateRangeSmart(this.getSubmissionDateRange(contest), opts);
  };

  /** ★追加：UI用（省略あり）採点期間ラベル */
  static getScoringPeriodLabelSmart = (
    contest: ContestResponseData,
    opts: RangeFormatOptions = {}
  ): string | null => {
    return this.formatDateRangeSmart(this.getScoringDateRange(contest), opts);
  };

  static getComingSoonLabel = (
    contest: ContestResponseData,
    format = "YYYY/MM/DD",
    suffix = "〜"
  ): string | null => {
    const { startDate } = contest;
    if (!startDate) return null;

    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return null;

    return `${start.format(format)}${suffix}`;
  };

  /** ★追加：英語向け coming soon（例: "Jan 22 ’26~"） */
  static getComingSoonLabelSmart = (
    contest: ContestResponseData,
    locale: Locale = "ja"
  ): string | null => {
    const { startDate } = contest;
    if (!startDate) return null;

    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return null;

    if (locale === "ja") return `${start.format("YYYY/MM/DD")}〜`;
    return `${start.format("MMM D ’YY")}~`;
  };

  /**
   * 開催終了（終了日）のラベルを返す
   * 例: "〜2026/01/10"
   */
  static getClosedLabel = (
    contest: ContestResponseData,
    format = "YYYY/MM/DD",
    prefix = "〜"
  ): string | null => {
    const { contestEndDate } = contest;
    if (!contestEndDate) return null;

    const end = dayjs(contestEndDate, "YYYY/MM/DD");
    if (!end.isValid()) return null;

    return `${prefix}${end.format(format)}`;
  };

  /** 英語向け closed label（例: "~ Jan 10 ’26"） */
  static getClosedLabelSmart = (
    contest: ContestResponseData,
    locale: Locale = "ja"
  ): string | null => {
    const { contestEndDate } = contest;
    if (!contestEndDate) return null;

    const end = dayjs(contestEndDate, "YYYY/MM/DD");
    if (!end.isValid()) return null;

    if (locale === "ja") return `〜${end.format("YYYY/MM/DD")}`;
    return `~ ${end.format("MMM D ’YY")}`;
  };

  static getContestDateRange = (contest: ContestResponseData): DateRange => {
    const { startDate, contestEndDate } = contest;
    if (!startDate) return { start: null, end: null };

    const start = dayjs(startDate, "YYYY/MM/DD");
    if (!start.isValid()) return { start: null, end: null };

    if (!contestEndDate) return { start, end: null };

    const end = dayjs(contestEndDate, "YYYY/MM/DD");
    if (!end.isValid()) return { start, end: null };

    return { start, end };
  };

  static getContestPeriodLabelSmart = (
    contest: ContestResponseData,
    opts: RangeFormatOptions = {}
  ): string | null => {
    return this.formatDateRangeSmart(this.getContestDateRange(contest), opts);
  };
}